import { Facebook, Globe, Instagram, Linkedin, Mail, MessageCircle, Save, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export function Field({ label, field, settings, update, textarea, placeholder, testid, rows, hint, icon }) {
  const Comp = textarea ? Textarea : Input;
  return (
    <>
      <label>{icon}{icon ? " " : null}{label}</label>
      <Comp value={settings[field] || ""} onChange={(e) => update(field, e.target.value)} placeholder={placeholder} rows={rows} data-testid={testid} />
      {hint && <small className="admin-field-hint">{hint}</small>}
    </>
  );
}

function ImageField({ label, field, settings, update, uploadImage, testid }) {
  return (
    <>
      <label>{label}</label>
      <Input value={settings[field] || ""} onChange={(e) => update(field, e.target.value)} data-testid={`${testid}-url-input`} />
      <input type="file" accept="image/*" onChange={(e) => uploadImage(field, e.target.files?.[0])} data-testid={`${testid}-file-input`} />
    </>
  );
}

export function WebsiteEditorPanel({ settings, update, saveSettings }) {
  const f = { settings, update };
  return (
    <section className="admin-panel wide" data-testid="admin-website-editor-panel">
      <h2>Website detail editor</h2>
      <Field {...f} label="Brand expansion" field="brand_expansion" testid="admin-brand-expansion-input" />
      <Field {...f} label="Hero badge" field="hero_badge" testid="admin-hero-badge-input" />
      <Field {...f} label="Hero headline" field="hero_headline" testid="admin-hero-headline-input" />
      <Field {...f} label="Hero subtext" field="hero_subtext" textarea testid="admin-hero-subtext-textarea" />
      <Field {...f} label="Primary tagline" field="tagline_primary" testid="admin-tagline-primary-input" />
      <Field {...f} label="Secondary tagline" field="tagline_secondary" testid="admin-tagline-secondary-input" />
      <Field {...f} label="Founder section title" field="founder_title" testid="admin-founder-title-input" />
      <Field {...f} label="Founder section copy" field="founder_copy" textarea testid="admin-founder-copy-textarea" />
      <Button className="primary-cta save-app" onClick={saveSettings} data-testid="admin-save-settings-button"><Save size={17} /> Save website details</Button>
    </section>
  );
}

export function FoundersPanel({ settings, update, saveSettings, uploadImage }) {
  const f = { settings, update };
  return (
    <section className="admin-panel" data-testid="admin-founders-panel">
      <h2>Founders</h2>
      {["one", "two"].map((k, i) => (
        <div key={k} className="admin-founder-block">
          <Field {...f} label={`Founder ${i + 1} name`} field={`founder_${k}_name`} testid={`admin-founder-${k}-name-input`} />
          <Field {...f} label={`Founder ${i + 1} role`} field={`founder_${k}_role`} testid={`admin-founder-${k}-role-input`} />
          <Field {...f} label={`Founder ${i + 1} bio`} field={`founder_${k}_bio`} textarea testid={`admin-founder-${k}-bio-input`} />
          <Field {...f} label={`Founder ${i + 1} LinkedIn URL`} field={`founder_${k}_linkedin`} placeholder="https://linkedin.com/in/…" testid={`admin-founder-${k}-linkedin-input`} />
          <ImageField {...f} uploadImage={uploadImage} label={`Founder ${i + 1} photo URL / upload`} field={`founder_${k}_image`} testid={`admin-founder-${k}-image`} />
        </div>
      ))}
      <Button className="primary-cta save-app" onClick={saveSettings} data-testid="admin-save-founders-button"><Save size={17} /> Save founders</Button>
    </section>
  );
}

const SOCIALS = [
  ["whatsapp_link", "WhatsApp", MessageCircle, "https://wa.me/91XXXXXXXXXX"],
  ["instagram_link", "Instagram", Instagram, "https://instagram.com/vomarc"],
  ["facebook_link", "Facebook", Facebook, "https://facebook.com/vomarc"],
  ["linkedin_link", "LinkedIn", Linkedin, "https://linkedin.com/company/vomarc"],
  ["google_link", "Google Business / Maps", Globe, "https://g.co/kgs/..."],
  ["gmail_link", "Gmail / Contact email link", Mail, "mailto:vision@vomarc.com"],
];

export function LinksPanel({ settings, update, saveSettings }) {
  const f = { settings, update };
  const embedHint = (name) => `When provided, clicking the ${name} card opens the embedded HTML in a modal. Link is used as a fallback.`;
  return (
    <section className="admin-panel" data-testid="admin-links-panel">
      <h2>Product redirect links</h2>
      <Field {...f} label="Book demo link (calendar / form URL)" field="book_demo_link" placeholder="https://calendly.com/vomarc" testid="admin-book-demo-link-input" />
      <Field {...f} label="Book demo embed HTML (optional, opens in a modal)" field="book_demo_embed" textarea rows={3} placeholder='<iframe src="https://calendly.com/…"></iframe>' testid="admin-book-demo-embed-textarea" hint="If empty, the Book demo button scrolls to the contact form." />
      <Field {...f} label="VOMARC App link" field="vomarc_app_link" placeholder="https://app.vomarc.com" testid="admin-vomarc-app-link-input" />
      <Field {...f} label="VOMARC App embed HTML or iframe (optional)" field="vomarc_app_embed" textarea rows={4} placeholder='Paste full HTML or iframe code, e.g. <iframe src="https://app.vomarc.com" ...></iframe>' testid="admin-vomarc-app-embed-textarea" hint={embedHint("VOMARC App")} />
      <Field {...f} label="VOMARC FSM link" field="vomarc_fsm_link" placeholder="https://fsm.vomarc.com" testid="admin-vomarc-fsm-link-input" />
      <Field {...f} label="VOMARC FSM embed HTML or iframe (optional)" field="vomarc_fsm_embed" textarea rows={4} placeholder='Paste full HTML or iframe code, e.g. <iframe src="https://fsm.vomarc.com" ...></iframe>' testid="admin-vomarc-fsm-embed-textarea" hint={embedHint("VOMARC FSM")} />
      <h2 style={{ marginTop: 18 }}>Call button hours (IST)</h2>
      <div className="admin-two-col">
        <div><Field {...f} label="Show call button from" field="call_hours_start" placeholder="09:00" testid="admin-call-hours-start-input" /></div>
        <div><Field {...f} label="Hide call button after" field="call_hours_end" placeholder="17:30" testid="admin-call-hours-end-input" /></div>
      </div>
      <small className="admin-field-hint">24-hour format. Outside these hours the Call buttons are hidden and visitors see the WhatsApp option only.</small>
      <h2 style={{ marginTop: 18 }}>Social redirect links</h2>
      {SOCIALS.map(([field, label, Icon, placeholder]) => (
        <Field key={field} {...f} label={label} field={field} icon={<Icon size={13} />} placeholder={placeholder} testid={`admin-${field.replace("_link", "")}-link-input`} />
      ))}
      <Button className="primary-cta save-app" onClick={saveSettings} data-testid="admin-save-links-button"><Save size={17} /> Save all links</Button>
    </section>
  );
}

export function ImagesPanel({ settings, update, saveSettings, uploadImage }) {
  const f = { settings, update, uploadImage };
  return (
    <section className="admin-panel" data-testid="admin-image-upload-panel">
      <h2>Image upload</h2>
      <ImageField {...f} label="Hero image URL / upload" field="hero_image_url" testid="admin-hero-image" />
      <ImageField {...f} label="Founder image URL / upload" field="founder_image_url" testid="admin-founder-image" />
      <Button className="ghost-cta-dark" onClick={saveSettings} data-testid="admin-save-images-button"><Upload size={17} /> Save uploaded images</Button>
    </section>
  );
}
